import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Library, Search, Download, BookOpen, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { AddItemDialog, LoansTab, MyLoans } from "./BibliothequeExtras";

const CAT_COLORS = {
  "Manuel scolaire": "bg-blue-100 text-blue-700",
  "Cahier d'exercices": "bg-green-100 text-green-700",
  "Roman / Lecture": "bg-purple-100 text-purple-700",
  "Document pédagogique": "bg-teal-100 text-teal-700",
  "Sujet d'examen": "bg-amber-100 text-amber-700",
  "Règlement / Administratif": "bg-slate-200 text-slate-600",
  "Autre": "bg-slate-100 text-slate-500",
};

const fmtSize = (b) => !b ? null : b > 1e6 ? `${(b / 1e6).toFixed(1)} Mo` : `${Math.round(b / 1e3)} Ko`;

const ItemCard = ({ item, canManage, onDelete, onDownload, downloading }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col gap-2 hover:shadow-md transition-shadow" data-testid={`library-item-${item.id}`}>
    <div className="flex items-start justify-between gap-2">
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CAT_COLORS[item.category] || CAT_COLORS.Autre}`}>{item.category}</span>
      {item.copies_total > 0 && (
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.copies_available > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {item.copies_available}/{item.copies_total} ex.
        </span>
      )}
    </div>
    <div>
      <div className="font-heading font-bold text-[#1E3A8A] leading-tight">{item.title}</div>
      {item.author && <div className="text-xs text-slate-500">{item.author}</div>}
    </div>
    {item.description && <p className="text-xs text-slate-500 line-clamp-2">{item.description}</p>}
    <div className="flex flex-wrap gap-1">
      {item.subject && <span className="text-[10px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-600">{item.subject}</span>}
      {(item.levels || []).map(l => <span key={l} className="text-[10px] bg-[#1E3A8A]/10 rounded-full px-2 py-0.5 text-[#1E3A8A] font-semibold">{l}</span>)}
      {(item.levels || []).length === 0 && <span className="text-[10px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-400">Tous niveaux</span>}
    </div>
    <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100">
      <span className="text-[10px] text-slate-400">{fmtSize(item.size) || "Physique uniquement"}{item.downloads ? ` · ${item.downloads} téléch.` : ""}</span>
      <div className="flex gap-2">
        {item.file_path && (
          <Button size="sm" onClick={() => onDownload(item)} disabled={downloading === item.id}
            className="bg-[#166534] text-white gap-1 h-7 text-xs" data-testid={`download-${item.id}`}>
            {downloading === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><BookOpen className="h-3 w-3" />Lire</>}
          </Button>
        )}
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => onDelete(item)} className="text-red-600 border-red-200 h-7 text-xs" data-testid={`delete-item-${item.id}`}>
            Suppr.
          </Button>
        )}
      </div>
    </div>
  </div>
);

export default function BibliothequePage() {
  const { user } = useAuth();
  const canManage = ["admin", "super_admin", "director", "scolarite"].includes(user?.role);
  const isFamily = ["student", "parent"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], levels: [], audiences: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [level, setLevel] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [seeding, setSeeding] = useState(false);

  const seedCatalog = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/library/seed-catalog");
      toast.success(data.message || "Import du catalogue lancé.");
      setTimeout(() => { load(); setSeeding(false); }, 15000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import du catalogue");
      setSeeding(false);
    }
  };

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (cat !== "all") params.category = cat;
      if (level !== "all") params.level = level;
      const { data } = await api.get("/library/items", { params });
      setItems(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de chargement"); }
  };
  useEffect(() => { load(); }, [cat, level]);
  useEffect(() => { api.get("/library/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const download = async (item) => {
    setDownloading(item.id);
    try {
      const resp = await api.get(`/library/items/${item.id}/file`, { responseType: "blob" });
      const ct = resp.headers["content-type"] || "";
      const url = URL.createObjectURL(new Blob([resp.data], { type: ct }));
      // Coin lecture : enregistre automatiquement la lecture en cours pour l'élève
      if (user?.role === "student") {
        api.post("/reading/progress", { item_id: item.id, current_page: 1, status: "en_cours" }).catch(() => {});
      }
      if (ct.includes("pdf") || ct.startsWith("image/")) {
        window.open(url, "_blank"); // lecture en ligne dans le navigateur
      } else {
        const a = document.createElement("a"); // EPUB & autres : téléchargement nommé
        a.href = url;
        a.download = item.file_name || `${item.title}.epub`;
        a.click();
        toast.success("Livre téléchargé — ouvrez-le avec votre application de lecture (EPUB).");
      }
    } catch { toast.error("Téléchargement impossible"); }
    finally { setDownloading(""); }
  };

  const del = async (item) => {
    if (!window.confirm(`Supprimer « ${item.title} » du catalogue ?`)) return;
    try { await api.delete(`/library/items/${item.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const catalog = (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Titre, auteur, matière..." className="pl-9" data-testid="library-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-52" data-testid="library-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {!isFamily && (
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-44" data-testid="library-level-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous niveaux</SelectItem>
              {meta.levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" onClick={load} data-testid="library-search-btn">Rechercher</Button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400" data-testid="library-empty">
          Aucun document dans le catalogue pour vos critères.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map(i => <ItemCard key={i.id} item={i} canManage={canManage} onDelete={del} onDownload={download} downloading={downloading} />)}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="bibliotheque-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Library className="h-3 w-3" /><span>BIBLIOTHÈQUE NUMÉRIQUE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Bibliothèque</h1>
          <p className="text-slate-500 mt-1">
            {isFamily ? "Documents accessibles selon votre classe et votre niveau." : "Catalogue numérique et gestion des emprunts d'exemplaires physiques."}
          </p>
          <p className="text-sm text-[#EA580C] font-semibold mt-1" data-testid="library-count-banner">
            {items.length > 0 && `${items.length} documents disponibles : romans intégraux, annales BEPC/BAC, manuels et guides — bonne lecture !`}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={seedCatalog} disabled={seeding} variant="outline" className="gap-1 border-[#1E3A8A] text-[#1E3A8A]" data-testid="seed-catalog-btn">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Library className="h-4 w-4" />}Importer le catalogue Mazid
            </Button>
            <Button onClick={() => setAddOpen(true)} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-item-btn">
              <Plus className="h-4 w-4" />Ajouter un document
            </Button>
          </div>
        )}
      </div>

      {canManage ? (
        <Tabs defaultValue="catalogue">
          <TabsList data-testid="library-tabs">
            <TabsTrigger value="catalogue" data-testid="tab-catalogue">Catalogue</TabsTrigger>
            <TabsTrigger value="emprunts" data-testid="tab-emprunts">Emprunts</TabsTrigger>
          </TabsList>
          <TabsContent value="catalogue" className="mt-4">{catalog}</TabsContent>
          <TabsContent value="emprunts" className="mt-4"><LoansTab items={items} onChanged={load} /></TabsContent>
        </Tabs>
      ) : (
        <>
          {catalog}
          {isFamily && <MyLoans />}
        </>
      )}

      <AddItemDialog open={addOpen} onOpenChange={setAddOpen} meta={meta} onCreated={load} />
    </div>
  );
}
